import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { Tooltip } from "@mui/material";
import { ReactNode, useState } from "react";

export interface FieldWarningIndicatorProps {
  /** Tooltip content shown on hover — string or rich ReactNode. */
  tooltip: ReactNode;
  /** Screen-reader label for the icon. */
  ariaLabel: string;
  /**
   * When the icon sits inline after a text value (e.g. "ABC ⚠️"), reserve a
   * small left margin so it doesn't crowd the value. Standalone uses (e.g.
   * AmountCell, which positions via parent Stack spacing) leave this off.
   */
  inline?: boolean;
  /**
   * Keep the tooltip open while the pointer moves onto it, so its contents are
   * clickable (e.g. the sources-disagree tooltip's copy-a-value affordance). The
   * app theme sets `disableInteractive: true` globally, so opt in here; the
   * `leaveDelay` bridges the small gap between the icon and the popup. Leave off
   * for read-only warnings.
   */
  interactive?: boolean;
  /**
   * Opens the tooltip without a pointer, for a caller that has just sent the
   * reviewer here — the extraction review's "jump to next" lands on a field and
   * shows why it was flagged, which for a record disagreement is also where the
   * two actions live.
   *
   * Passing it at all takes over the open state, so pass a boolean consistently
   * rather than switching between a value and `undefined`. Hover still works:
   * whichever of the two says open wins, which is what keeps the tooltip up when
   * the pointer moves onto it just as the field loses focus.
   */
  forceOpen?: boolean;
}

/**
 * Generic cell-level warning icon used wherever a field needs to flag an issue
 * — a ManifestDiscrepancy (any status), a silent source disagreement, etc. The visual
 * (warning-colored ErrorOutline) and tooltip behavior are shared so the user
 * sees a single, consistent affordance regardless of what triggered it.
 *
 * Render decision lives with the caller: this component always renders when
 * mounted. Callers gate on whatever signal is meaningful for them.
 */
export function FieldWarningIndicator({
  tooltip,
  ariaLabel,
  inline = false,
  interactive = false,
  forceOpen,
}: FieldWarningIndicatorProps) {
  // Only tracked for a caller that supplies `forceOpen`; everyone else leaves the
  // tooltip uncontrolled and behaves exactly as before.
  const [pointerOpen, setPointerOpen] = useState(false);
  const controlled = forceOpen !== undefined;

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      enterDelay={400}
      disableInteractive={!interactive}
      leaveDelay={interactive ? 200 : undefined}
      open={controlled ? forceOpen || pointerOpen : undefined}
      onOpen={controlled ? () => setPointerOpen(true) : undefined}
      onClose={controlled ? () => setPointerOpen(false) : undefined}
      slotProps={
        controlled
          ? {
              // The trigger's own hover is reported by onOpen/onClose, but a
              // pointer that arrives straight onto the tooltip body — which is
              // what happens when travel opened it and the reviewer reaches for a
              // value in it — is not. Without these the tooltip would close under
              // the cursor the moment the field lost focus.
              tooltip: {
                onMouseEnter: () => setPointerOpen(true),
                onMouseLeave: () => setPointerOpen(false),
              },
            }
          : undefined
      }
    >
      <ErrorOutline
        sx={{
          fontSize: "1rem",
          color: "warning.main",
          flexShrink: 0,
          ...(inline ? { ml: 0.5 } : {}),
        }}
        aria-label={ariaLabel}
      />
    </Tooltip>
  );
}
